import type { WebhookResponse } from '@monorepo/shared/schemas/webhooks'
import { createFileRoute } from '@tanstack/react-router'
import { Activity, Code, Home, Webhook as WebhookIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AddWebhookDrawer } from '@/features/developers/components/AddWebhookDrawer'
import { WebhookLogsTab } from '@/features/developers/components/WebhookLogsTab'
import { WebhookTable } from '@/features/developers/components/WebhookTable'
import { webhookService } from '@/features/developers/services/webhookService'
import { PageTitle } from '@/shared/components/common/PageTitle'
import {
  SubtleTabs,
  SubtleTabsContent,
  SubtleTabsList,
  SubtleTabsTrigger,
} from '@/shared/components/common/tabs/SubtleTabs'
import { Badge } from '@/shared/components/ui/badge'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/shared/components/ui/breadcrumb'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/components/ui/dialog'
import { useBreadcrumbs } from '@/shared/hooks/useBreadcrumbs'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'

export const Route = createFileRoute('/_auth/developers/webhooks')({
  component: WebhooksPage,
})

function WebhooksPage() {
  const [webhooks, setWebhooks] = useState<WebhookResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('webhooks')
  const [showPayloadDialog, setShowPayloadDialog] = useState(false)
  const { setBreadcrumbs } = useBreadcrumbs()

  const activeCount = webhooks.filter(w => w.active).length
  const totalCount = webhooks.length

  const loadWebhookData = useCallback(async () => {
    setLoading(true)
    try {
      const result = await webhookService.listWebhooks({ limit: 100 })
      setWebhooks(result.data)
    } catch (error) {
      toast.error(extractErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }, [])

  const breadcrumbsContent = useMemo(
    () => (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink to='/dashboard' className='flex items-center gap-1'>
              <Home className='w-4 h-4' />
              Dashboard
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <span className='text-foreground font-normal'>Developers</span>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Webhooks</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    ),
    []
  )

  useEffect(() => {
    setBreadcrumbs(breadcrumbsContent)
  }, [setBreadcrumbs, breadcrumbsContent])

  useEffect(() => {
    loadWebhookData()
  }, [loadWebhookData])

  if (loading) {
    return (
      <div className='container mx-auto py-6'>
        <div className='space-y-6'>
          {/* Header - Static, no skeleton */}
          <div className='flex flex-col gap-4 md:flex-row md:justify-between md:items-start'>
            <div>
              <PageTitle
                title='Webhooks'
                subtitle='Configure webhooks for real-time notifications and integrations'
              />
            </div>
            <div className='flex gap-3 items-center w-full md:w-auto'>
              <Dialog
                open={showPayloadDialog}
                onOpenChange={setShowPayloadDialog}
              >
                <DialogTrigger asChild>
                  <Button variant='outline' size='sm' className='gap-2'>
                    <Code className='w-4 h-4' />
                    Payload Format
                  </Button>
                </DialogTrigger>
                <DialogContent className='sm:max-w-2xl'>
                  <DialogHeader>
                    <DialogTitle>Webhook Payload Format</DialogTitle>
                  </DialogHeader>
                  <div className='mt-4'>
                    <div className='bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4'>
                      <pre className='text-sm text-blue-800 dark:text-blue-200 font-mono overflow-x-auto'>
                        {`{
  "event": "agent.task.completed",
  "data": {
    "agent_id": "agent-123",
    "task_id": "task-456",
    "result": "success"
  },
  "timestamp": "2024-08-28T12:00:00Z",
  "webhook_id": "wh-789"
}`}
                      </pre>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              <AddWebhookDrawer onWebhookCreated={loadWebhookData} />
            </div>
          </div>

          {/* Tabs - Static, no skeleton */}
          <SubtleTabs value={activeTab} onValueChange={setActiveTab}>
            <SubtleTabsList>
              <SubtleTabsTrigger
                value='webhooks'
                className='flex items-center gap-2'
              >
                <WebhookIcon className='w-4 h-4' />
                Webhooks
              </SubtleTabsTrigger>
              <SubtleTabsTrigger
                value='logs'
                className='flex items-center gap-2'
              >
                <Activity className='w-4 h-4' />
                Event Logs
              </SubtleTabsTrigger>
            </SubtleTabsList>

            {/* Table Skeleton - Only for dynamic data */}
            <SubtleTabsContent value='webhooks'>
              <div className='bg-transparent border rounded-lg'>
                <div className='animate-pulse space-y-4 p-6'>
                  <div className='grid grid-cols-6 gap-4 pb-4 border-b'>
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div
                        // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                        key={i}
                        className='h-4 bg-slate-300 dark:bg-slate-600 rounded'
                      ></div>
                    ))}
                  </div>
                  {Array.from({ length: 5 }).map((_, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                    <div key={i} className='grid grid-cols-6 gap-4 py-3'>
                      {Array.from({ length: 6 }).map((_, j) => (
                        <div
                          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                          key={j}
                          className='h-4 bg-slate-300 dark:bg-slate-600 rounded'
                        ></div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </SubtleTabsContent>

            <SubtleTabsContent value='logs'>
              <div className='bg-transparent border rounded-lg'>
                <div className='animate-pulse space-y-4 p-6'>
                  <div className='grid grid-cols-4 gap-4 pb-4 border-b'>
                    {Array.from({ length: 4 }).map((_, i) => (
                      <div
                        // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                        key={i}
                        className='h-4 bg-slate-300 dark:bg-slate-600 rounded'
                      ></div>
                    ))}
                  </div>
                  {Array.from({ length: 5 }).map((_, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                    <div key={i} className='grid grid-cols-4 gap-4 py-3'>
                      {Array.from({ length: 4 }).map((_, j) => (
                        <div
                          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                          key={j}
                          className='h-4 bg-slate-300 dark:bg-slate-600 rounded'
                        ></div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </SubtleTabsContent>
          </SubtleTabs>
        </div>
      </div>
    )
  }

  return (
    <div className='container mx-auto py-6'>
      <div className='space-y-6'>
        {/* Header */}
        <div className='flex flex-col gap-4 md:flex-row md:justify-between md:items-start'>
          <div>
            <PageTitle
              title='Webhooks'
              subtitle='Configure webhooks for real-time notifications and integrations'
            />
            {/* Stats Chips */}
            <div className='flex flex-wrap gap-2 mt-3'>
              <Badge variant='secondary' className='text-sm'>
                {totalCount} Total
              </Badge>
              <Badge variant='default' className='text-sm'>
                {activeCount} Active
              </Badge>
            </div>
          </div>
          <div className='flex gap-3 items-center w-full md:w-auto'>
            <Dialog
              open={showPayloadDialog}
              onOpenChange={setShowPayloadDialog}
            >
              <DialogTrigger asChild>
                <Button variant='outline' size='sm' className='gap-2'>
                  <Code className='w-4 h-4' />
                  Payload Format
                </Button>
              </DialogTrigger>
              <DialogContent className='sm:max-w-2xl'>
                <DialogHeader>
                  <DialogTitle>Webhook Payload Format</DialogTitle>
                </DialogHeader>
                <div className='mt-4'>
                  <div className='bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4'>
                    <pre className='text-sm text-blue-800 dark:text-blue-200 font-mono overflow-x-auto'>
                      {`{
  "event": "agent.task.completed",
  "data": {
    "agent_id": "agent-123",
    "task_id": "task-456",
    "result": "success"
  },
  "timestamp": "2024-08-28T12:00:00Z",
  "webhook_id": "wh-789"
}`}
                    </pre>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            <AddWebhookDrawer onWebhookCreated={loadWebhookData} />
          </div>
        </div>

        {/* Main Content */}
        <SubtleTabs value={activeTab} onValueChange={setActiveTab}>
          <SubtleTabsList>
            <SubtleTabsTrigger
              value='webhooks'
              className='flex items-center gap-2'
            >
              <WebhookIcon className='w-4 h-4' />
              Webhooks
            </SubtleTabsTrigger>
            <SubtleTabsTrigger value='logs' className='flex items-center gap-2'>
              <Activity className='w-4 h-4' />
              Event Logs
            </SubtleTabsTrigger>
          </SubtleTabsList>

          {/* Webhooks Tab */}
          <SubtleTabsContent value='webhooks'>
            <div className='[&_table]:bg-transparent [&_thead]:bg-transparent [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50'>
              <WebhookTable
                webhooks={webhooks}
                onWebhookUpdated={loadWebhookData}
                onWebhookDeleted={loadWebhookData}
              />
            </div>

            {/* Empty State */}
            {webhooks.length === 0 && (
              <div className='text-center py-12'>
                <WebhookIcon className='mx-auto h-12 w-12 text-muted-foreground mb-4' />
                <h3 className='text-lg font-semibold mb-2'>
                  No webhooks configured
                </h3>
                <p className='text-muted-foreground mb-4'>
                  Get started by creating your first webhook to receive
                  real-time notifications.
                </p>
                <AddWebhookDrawer onWebhookCreated={loadWebhookData} />
              </div>
            )}
          </SubtleTabsContent>

          {/* Logs Tab */}
          <SubtleTabsContent value='logs'>
            <WebhookLogsTab webhooks={webhooks} />
          </SubtleTabsContent>
        </SubtleTabs>
      </div>
    </div>
  )
}
